import React, { Fragment, useState } from 'react';

import { Dialog, Transition } from '@headlessui/react';
import Image from 'next/image';
import { HiClipboardCheck, HiClipboardCopy } from 'react-icons/hi';
import {
  FacebookIcon,
  FacebookMessengerIcon,
  FacebookMessengerShareButton,
  FacebookShareButton,
  TwitterIcon,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
} from 'react-share';
import { useCopyToClipboard } from 'usehooks-ts';

interface Props {
  title: string;
  shareUrl: string;
  socialIconSize?: number;
  buttonElement: JSX.Element;
}

const SocialShare = ({
  title,
  shareUrl,
  socialIconSize,
  buttonElement,
}: Props) => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [copiedText, setCopiedText] = useCopyToClipboard();

  function closeModal() {
    setModalIsOpen(false);
    setCopiedText('');
  }

  function openModal() {
    setModalIsOpen(true);
  }
  return (
    <>
      {React.cloneElement(buttonElement, {
        onClick: (e: any) => {
          e.preventDefault();
          openModal();
        },
      })}
      <Transition appear show={modalIsOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-white bg-opacity-50 backdrop-blur-md" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="flex flex-col justify-center items-center transform overflow-hidden rounded-2xl border border-secondary/50 bg-white p-2 lg:p-6 text-left align-middle shadow-xl transition-all">
                  <div
                    className="btn btn-sm btn-circle absolute right-2 top-2"
                    onClick={closeModal}
                  >
                    ✕
                  </div>
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-bold leading-6 mb-2"
                  >
                    {title}
                  </Dialog.Title>
                  <Image
                    width={200}
                    height={200}
                    src="/media/adShare.webp"
                    alt="tayara share illustration"
                    unoptimized
                    className="lg:w-fit mt-8 w-1/2"
                  />
                  <div className="w-full flex flex-wrap lg:gap-2 justify-center items-center">
                    <button
                      className="flex flex-col items-center bg-transparent hover:bg-neutral-100 text-sm font-medium cursor-pointer rounded-2xl p-2 lg:p-2"
                      onClick={() => {
                        setCopiedText(shareUrl || 'Erreur de copie');
                      }}
                    >
                      {copiedText ? (
                        <span className="flex flex-col justify-center items-center text-emerald-500">
                          <HiClipboardCheck size={socialIconSize || 32} />
                          <p className="mt-1">Lien copié</p>
                        </span>
                      ) : (
                        <span className="flex flex-col justify-center items-center font-semibold">
                          <HiClipboardCopy
                            className="text-blue-600"
                            size={socialIconSize || 32}
                          />
                          <p className="mt-1">Copier lien</p>
                        </span>
                      )}
                    </button>
                    <FacebookShareButton
                      url={shareUrl}
                      quote={title}
                      className="flex flex-col items-center !bg-transparent hover:!bg-neutral-100 !font-semibold !text-sm !cursor-pointer !rounded-2xl !p-2 !lg:p-4"
                    >
                      <FacebookIcon size={socialIconSize || 32} round />
                      <p className="mt-1">Facebook</p>
                    </FacebookShareButton>
                    <FacebookMessengerShareButton
                      url={shareUrl}
                      appId="521270401588372"
                      className="flex flex-col items-center !bg-transparent hover:!bg-neutral-100 !font-semibold !text-sm !cursor-pointer !rounded-2xl !p-2 !lg:p-4"
                    >
                      <FacebookMessengerIcon
                        size={socialIconSize || 32}
                        round
                      />
                      <p className="mt-1">Messenger</p>
                    </FacebookMessengerShareButton>
                    <WhatsappShareButton
                      url={shareUrl}
                      className="flex flex-col items-center !bg-transparent hover:!bg-neutral-100 !font-semibold !text-sm !cursor-pointer !rounded-2xl !p-2 !lg:p-4"
                    >
                      <WhatsappIcon size={socialIconSize || 32} round />
                      <p className="mt-1">Whatsapp</p>
                    </WhatsappShareButton>
                    <TwitterShareButton
                      url={shareUrl}
                      className="flex flex-col items-center !bg-transparent hover:!bg-neutral-100 !font-semibold !text-sm !cursor-pointer !rounded-2xl !p-2 !lg:p-4"
                    >
                      <TwitterIcon size={socialIconSize || 32} round />
                      <p className="mt-1">Twitter</p>
                    </TwitterShareButton>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default SocialShare;
